import { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';

import {
  Affiliation,
  AffiliationService,
  GroupEntityWithStats as GroupEntity,
  Parent,
  ParentAffiliationService,
  ParentSchoolsService,
  School,
  SchoolService,
  Sitter,
  SitterService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

type GroupType = 'friend' | 'company' | 'localGroup' | 'school' | 'affiliation' | 'group';

interface GroupRow {
  type: GroupType;
  entity: GroupEntity;
  hidePhotos?: boolean;
  href?: string;
}

const RECOMMENDATION_LIMIT = 16;

const GroupSnippet = ({ row }: { row: GroupRow }): JSX.Element => {
  const { t } = useTranslation();
  const entity = row.entity;
  if (!entity.size && !row.hidePhotos) return <></>;
  const name = entity.name ?? '';
  const size = entity.size ?? 0;
  const suffix =
    row.type === 'friend'
      ? t('pages.exploreLists.friendTypeLabel')
      : t('pages.exploreLists.groupTypeLabel');
  const noun = size === 1
    ? t('pages.exploreLists.caregiverSingular')
    : t('pages.exploreLists.caregiverPlural');

  const inner = (
    <>
      <h3
        id={`${row.type}-name-${entity.id ?? 0}`}
        className='text-lg font-semibold mb-1 text-neutral-800'
      >
        {name}
      </h3>
      <div className='text-sm text-neutral-600 mb-3'>
        <span className='font-semibold text-neutral-800'>{size}</span>
        <span>{suffix}</span>
        <span>{noun}</span>
      </div>
      {!entity.alias && row.type !== 'friend' && (
        <div className='inline-block text-xs font-semibold text-sapphire bg-sapphire-50 px-2 py-0.5 rounded-full mb-3'>
          {t('pages.exploreLists.comingSoon')}
        </div>
      )}
      {!row.hidePhotos && (entity.sitters ?? []).length > 0 && (
        <div className='flex -space-x-2 mt-1'>
          {(entity.sitters ?? []).slice(0, 3).map((s: Sitter) => (
            <img
              key={s.id}
              src={s.pictureUrl}
              alt={s.firstName ?? ''}
              className='w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm'
            />
          ))}
        </div>
      )}
    </>
  );

  const cardClass =
    'block bg-white border border-neutral-200 rounded-lg p-5 shadow-sm transition-all h-full';

  if (row.href) {
    return (
      <Link
        to={row.href}
        className={`${cardClass} hover:shadow-md hover:border-sapphire-100 no-underline text-neutral-800`}
      >
        {inner}
      </Link>
    );
  }
  return <div className={cardClass}>{inner}</div>;
};

// Parent is enriched at runtime with legacy fields (myFriends, company size)
// that aren't part of the typed Parent shape. Keep this localized intersection
// rather than polluting the shared type.
type ExploreParent = Parent & {
  myFriends?: { id?: number; name?: string; size?: number; sitters?: Sitter[] };
  companyEmployee?: Parent['companyEmployee'] & {
    company?: NonNullable<Parent['companyEmployee']>['company'] & {
      size?: number;
      sitters?: Sitter[];
    };
  };
};

interface TypeaheadItem {
  id?: number;
  name?: string;
  subtitle?: string;
}

const Typeahead = ({
  placeholder,
  search,
  onSelect,
  onCreate,
  createLabel,
  noMatchesHint,
}: {
  placeholder: string;
  search: (q: string) => Promise<TypeaheadItem[]>;
  onSelect: (item: TypeaheadItem) => Promise<void>;
  onCreate?: (name: string) => Promise<void>;
  createLabel?: string;
  noMatchesHint?: string;
}): JSX.Element => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TypeaheadItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      void (async (): Promise<void> => {
        try {
          const r = await search(query.trim());
          setResults(r);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 250);
    return (): void => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = async (item: TypeaheadItem): Promise<void> => {
    setSubmitting(true);
    try {
      await onSelect(item);
      setQuery('');
      setResults([]);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async (): Promise<void> => {
    if (!onCreate || !query.trim()) return;
    setSubmitting(true);
    try {
      await onCreate(query.trim());
      setQuery('');
      setResults([]);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='relative'>
      <div className='relative'>
        <FontAwesomeIcon
          icon={faSearch}
          className='absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400'
          aria-hidden='true'
        />
        <input
          type='text'
          className='w-full border border-neutral-300 rounded-md pl-9 pr-3 py-2.5 focus:outline-none focus:border-sapphire focus:ring-1 focus:ring-sapphire'
          placeholder={placeholder}
          value={query}
          onChange={(e): void => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={(): void => setOpen(true)}
          onBlur={(): void => {
            setTimeout(() => setOpen(false), 200);
          }}
          disabled={submitting}
        />
      </div>
      {open && query.trim().length >= 2 && (
        <div className='absolute z-10 left-0 right-0 bg-white border border-neutral-200 rounded-md shadow-lg mt-1 max-h-64 overflow-auto'>
          {loading && (
            <div className='px-3 py-2 text-sm text-neutral-500'>Searching…</div>
          )}
          {!loading && results.slice(0, 8).map((r) => (
            <Button
              key={r.id}
              buttonType={ButtonType.tertiary}
              onMouseDown={(): Promise<void> => handleSelect(r)}
              className='!block w-full text-left !px-3 !py-2 hover:!bg-neutral-100 !text-neutral-800 !rounded-none'
            >
              <div className='font-semibold text-sm'>{r.name}</div>
              {r.subtitle && (
                <div className='text-xs text-neutral-500'>{r.subtitle}</div>
              )}
            </Button>
          ))}
          {!loading && results.length === 0 && onCreate && createLabel && (
            <Button
              buttonType={ButtonType.tertiary}
              onMouseDown={handleCreate}
              className='!block w-full text-left !px-3 !py-2 hover:!bg-neutral-100 !text-sapphire !rounded-none'
            >
              {createLabel}: <span className='font-semibold'>{query}</span>
            </Button>
          )}
          {!loading && results.length === 0 && !onCreate && (
            <div className='px-3 py-2 text-sm text-neutral-500'>
              {noMatchesHint ?? 'No matches.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ChipProps {
  label: string;
  onRemove: () => Promise<void> | void;
  ariaLabel: string;
}

const RemovableChip = ({ label, onRemove, ariaLabel }: ChipProps): JSX.Element => {
  const [busy, setBusy] = useState(false);
  const handleClick = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await onRemove();
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className='inline-flex items-center gap-2 bg-neutral-100 text-neutral-800 rounded-full pl-3 pr-1 py-1 text-sm'>
      <span className='max-w-[16rem] truncate'>{label}</span>
      <Button
        buttonType={ButtonType.tertiary}
        onClick={handleClick}
        disabled={busy}
        aria-label={ariaLabel}
        className='!w-6 !h-6 !min-w-0 !p-0 flex items-center justify-center !rounded-full hover:!bg-neutral-200'
      >
        <FontAwesomeIcon icon={faTimes} className='text-neutral-600 text-xs' />
      </Button>
    </span>
  );
};

interface SuggestionChipProps {
  label: string;
  onAdd: () => Promise<void> | void;
}

const SuggestionChip = ({ label, onAdd }: SuggestionChipProps): JSX.Element => {
  const [busy, setBusy] = useState(false);
  const handleClick = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await onAdd();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      buttonType={ButtonType.tertiary}
      onClick={handleClick}
      disabled={busy}
      className='!inline-flex items-center gap-1.5 !bg-sapphire-50 !text-sapphire !rounded-full !px-3 !py-1 !text-sm hover:!bg-sapphire-100 transition-colors'
    >
      <FontAwesomeIcon icon={faPlus} className='text-xs' aria-hidden='true' />
      <span>{label}</span>
    </Button>
  );
};

export const ExploreLists = observer((): JSX.Element => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const parent = stores.parent.parent as ExploreParent;

  const [rows, setRows] = useState<GroupRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [suggestedAffiliations, setSuggestedAffiliations] = useState<Affiliation[]>([]);

  useEffect(() => {
    document.title = 'UrbanSitter - Your Lists';
  }, []);

  useEffect(() => {
    if (searchParams.get('companyError') && !searchParams.get('shown')) {
      toast.error(t('pages.exploreLists.companyError'));
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const buildRows = async (): Promise<void> => {
      const out: GroupRow[] = [];

      const loadSitters = async (entity: GroupEntity, filterKey: string): Promise<GroupEntity> => {
        try {
          const resp = await SitterService.getByFilter({
            params: { userId: user.id, limit: 3, [filterKey]: entity.id },
          });
          return {
            ...entity,
            sitters: resp.data?.data ?? [],
            size: resp.data?.meta?.size ?? entity.size ?? 0,
          };
        } catch {
          return entity;
        }
      };

      if (parent?.myFriends?.size) {
        const enriched = await loadSitters(parent.myFriends, 'friendId');
        out.push({ type: 'friend', entity: enriched, href: '/sitters/friends' });
      }
      if (parent?.companyEmployee?.company?.size && parent.companyEmployee.company.alias) {
        const c = parent.companyEmployee.company;
        const enriched = await loadSitters(c, 'companyId');
        out.push({
          type: 'company',
          entity: enriched,
          hidePhotos: !c.alias,
          href: `/sitters/companies/${c.alias}`,
        });
      }
      for (const item of parent?.localGroups ?? []) {
        const g = item.localGroup;
        if (!g) continue;
        const enriched = await loadSitters(g, 'localGroupId');
        out.push({
          type: 'localGroup',
          entity: enriched,
          hidePhotos: !g.alias,
          href: g.alias ? `/sitters/local-groups/${g.alias}` : undefined,
        });
      }
      for (const item of parent?.schools ?? []) {
        const s = item.school;
        if (!s) continue;
        const enriched = await loadSitters(s, 'schoolId');
        out.push({
          type: 'school',
          entity: enriched,
          hidePhotos: !s.alias,
          href: s.alias ? `/sitters/schools/${s.alias}` : undefined,
        });
      }
      for (const item of parent?.affiliations ?? []) {
        const a = item.affiliation;
        if (!a) continue;
        const enriched = await loadSitters(a, 'affiliationId');
        out.push({
          type: 'group',
          entity: enriched,
          hidePhotos: !a.alias,
          href: a.alias ? `/sitters/affiliations/${a.alias}` : undefined,
        });
      }

      if (!cancelled) {
        setRows(out);
        setIsLoading(false);
      }
    };

    buildRows();
    return (): void => {
      cancelled = true;
    };
  }, [
    user?.id,
    parent?.schools?.length,
    parent?.affiliations?.length,
    parent?.localGroups?.length,
  ]);

  const addedAffiliationIds = useMemo(
    () => (parent?.affiliations ?? [])
      .map((a) => a.affiliation?.id)
      .filter((id): id is number => typeof id === 'number'),
    [parent?.affiliations],
  );

  // Mirror Angular `affiliation-select.initAffiliations` — fetch up to
  // RECOMMENDATION_LIMIT suggested groups for this parent's region,
  // excluding ones they've already added.
  useEffect(() => {
    if (!user?.id || !parent?.address?.city || !parent?.address?.state) {
      setSuggestedAffiliations([]);
      return;
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const resp = await AffiliationService.getByFilter({
          params: {
            city: parent.address?.city,
            state: parent.address?.state,
            parentId: user.id,
            recommendations: true,
            ignoreIds: addedAffiliationIds.join(','),
          },
        });
        if (!cancelled) {
          const data = (resp.data?.data ?? []).slice(0, RECOMMENDATION_LIMIT);
          setSuggestedAffiliations(data);
        }
      } catch {
        if (!cancelled) setSuggestedAffiliations([]);
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [
    user?.id,
    parent?.address?.city,
    parent?.address?.state,
    addedAffiliationIds.join(','),
  ]);

  const hasAnyGroup =
    rows.length > 0 &&
    rows.some(
      (r) => !!r.entity.size || r.type === 'school' || r.type === 'affiliation' || r.type === 'localGroup',
    );
  const hasSchoolsOrAffiliations =
    (parent?.schools?.length ?? 0) > 0 || (parent?.affiliations?.length ?? 0) > 0;

  const searchSchools = useCallback(
    async (q: string): Promise<TypeaheadItem[]> => {
      const ignoreIds = (parent?.schools ?? [])
        .map((s) => s.school?.id)
        .filter((id): id is number => typeof id === 'number')
        .join(',');
      const resp = await SchoolService.getByFilter({
        params: {
          name: q,
          city: parent?.address?.city,
          state: parent?.address?.state,
          ignoreIds,
        },
      });
      return (resp.data?.data ?? []).map((s: School) => ({
        id: s.id,
        name: s.name,
        subtitle: s.location
          ? `${s.location.name ?? ''}${s.location.state?.code ? `, ${s.location.state.code}` : ''}`
          : undefined,
      }));
    },
    [parent?.schools, parent?.address?.city, parent?.address?.state],
  );

  const onSelectSchool = useCallback(
    async (item: TypeaheadItem): Promise<void> => {
      if (!user?.id || !item.id) return;
      // Mirrors Angular parent-school-multi-select.add — backend expects
      // `{ parentId, school: { id } }`, not a flat `schoolId`.
      await ParentSchoolsService.create({
        parentId: user.id,
        school: { id: item.id },
      });
      await stores.parent.refreshParent();
      toast.success(t('pages.exploreLists.addedToast', { name: item.name ?? '' }));
    },
    [user?.id, stores.parent, t],
  );

  const onCreateSchool = useCallback(
    async (name: string): Promise<void> => {
      if (!user?.id) return;
      const created = await SchoolService.create({
        name,
        city: parent?.address?.city,
        state: parent?.address?.state,
      } as School);
      const newId = (created.data?.data as { id?: number } | undefined)?.id;
      if (newId) {
        await ParentSchoolsService.create({
          parentId: user.id,
          school: { id: newId },
        });
        await stores.parent.refreshParent();
        toast.success(t('pages.exploreLists.addedToast', { name }));
      }
    },
    [user?.id, parent?.address?.city, parent?.address?.state, stores.parent, t],
  );

  const onRemoveSchool = useCallback(
    async (parentSchoolId: number, name: string): Promise<void> => {
      await ParentSchoolsService.delete(parentSchoolId);
      await stores.parent.refreshParent();
      toast.success(t('pages.exploreLists.removedToast', { name }));
    },
    [stores.parent, t],
  );

  const searchAffiliations = useCallback(
    async (q: string): Promise<TypeaheadItem[]> => {
      const ignoreIds = addedAffiliationIds.join(',');
      const resp = await AffiliationService.getByFilter({
        params: {
          name: q,
          type: 'group',
          recommendations: true,
          parentId: user?.id,
          city: parent?.address?.city,
          state: parent?.address?.state,
          ignoreIds,
          limit: 20,
        },
      });
      return (resp.data?.data ?? []).map((a: Affiliation) => ({
        id: a.id,
        name: a.name,
      }));
    },
    [user?.id, parent?.address?.city, parent?.address?.state, addedAffiliationIds],
  );

  const onSelectAffiliation = useCallback(
    async (item: TypeaheadItem): Promise<void> => {
      if (!user?.id || !item.id) return;
      // Mirrors Angular affiliation-select.add — backend expects nested
      // `{ parentId, affiliation: { id } }`, not a flat `affiliationId`.
      await ParentAffiliationService.create({
        parentId: user.id,
        affiliation: { id: item.id },
      });
      await stores.parent.refreshParent();
      toast.success(t('pages.exploreLists.addedToast', { name: item.name ?? '' }));
    },
    [user?.id, stores.parent, t],
  );

  const onRemoveAffiliation = useCallback(
    async (parentAffiliationId: number, name: string): Promise<void> => {
      await ParentAffiliationService.delete(parentAffiliationId);
      await stores.parent.refreshParent();
      toast.success(t('pages.exploreLists.removedToast', { name }));
    },
    [stores.parent, t],
  );

  const addedSchools = parent?.schools ?? [];
  const addedAffiliations = parent?.affiliations ?? [];

  return (
    <div>
      <div
        className='py-12 md:py-16 md:min-h-[300px] lg:min-h-[400px] bg-sapphire bg-cover bg-center bg-no-repeat text-white flex items-center'
        style={{
          backgroundImage:
            'linear-gradient(rgba(8, 70, 124, 0.55), rgba(8, 70, 124, 0.55)), url(https://assets.urbansitter.com/static/img/backgrounds/city.webp)',
        }}
      >
        <div className='container max-w-3xl mx-auto text-center px-4'>
          <h1 className='text-3xl md:text-4xl font-semibold mb-4 capitalize text-white'>
            {t('pages.exploreLists.heading')}
          </h1>
          <p className='text-base md:text-lg text-white/95 mb-0'>
            {t('pages.exploreLists.intro')}
          </p>
        </div>
      </div>

      {isLoading && (
        <div className='container py-10'>
          <p className='text-neutral-600'>{t('pages.exploreLists.loading')}</p>
        </div>
      )}

      {!isLoading && hasAnyGroup && (
        <div className='container py-10'>
          <h2 className='text-2xl font-semibold mb-6'>
            {t('pages.exploreLists.yourListsHeading')}
          </h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5'>
            {rows.map((row, i) => (
              <GroupSnippet key={`${row.type}-${row.entity.id}-${i}`} row={row} />
            ))}
          </div>
          {hasSchoolsOrAffiliations && (
            <p className='text-xs text-neutral-500 mt-6'>
              {t('pages.exploreLists.disclaimer')}
            </p>
          )}
        </div>
      )}

      {!isLoading && (
        <div className='container py-10 border-t border-neutral-200'>
          <div className='mb-8 max-w-3xl'>
            <h2 className='text-2xl font-semibold mb-2'>
              {t('pages.exploreLists.manageHeading')}
            </h2>
            <p className='text-neutral-600 mb-0'>
              {t('pages.exploreLists.manageIntro')}
            </p>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl'>
            <section>
              <h3 className='text-lg font-semibold mb-1'>
                {t('pages.exploreLists.schoolsHeading')}
              </h3>
              <p className='text-sm text-neutral-600 mb-4'>
                {t('pages.exploreLists.schoolsBody')}
              </p>

              {addedSchools.length > 0 && (
                <div className='flex flex-wrap gap-2 mb-4'>
                  {addedSchools.map((ps) => (
                    ps.id && ps.school?.name && (
                      <RemovableChip
                        key={`school-${ps.id}`}
                        label={ps.school.name}
                        ariaLabel={t('pages.exploreLists.removeAriaLabel', { name: ps.school.name })}
                        onRemove={(): Promise<void> => onRemoveSchool(ps.id!, ps.school?.name ?? '')}
                      />
                    )
                  ))}
                </div>
              )}

              <Typeahead
                placeholder={t('pages.exploreLists.searchSchoolsPlaceholder')}
                search={searchSchools}
                onSelect={onSelectSchool}
                onCreate={onCreateSchool}
                createLabel={t('pages.exploreLists.cantFindSchool')}
              />
            </section>

            <section>
              <h3 className='text-lg font-semibold mb-1'>
                {t('pages.exploreLists.groupsHeading')}
              </h3>
              <p className='text-sm text-neutral-600 mb-4'>
                {t('pages.exploreLists.groupsBody')}
              </p>

              {addedAffiliations.length > 0 && (
                <div className='flex flex-wrap gap-2 mb-4'>
                  {addedAffiliations.map((pa) => (
                    pa.id && pa.affiliation?.name && (
                      <RemovableChip
                        key={`affiliation-${pa.id}`}
                        label={pa.affiliation.name}
                        ariaLabel={t('pages.exploreLists.removeAriaLabel', { name: pa.affiliation.name })}
                        onRemove={(): Promise<void> => onRemoveAffiliation(pa.id!, pa.affiliation?.name ?? '')}
                      />
                    )
                  ))}
                </div>
              )}

              {suggestedAffiliations.length > 0 && (
                <div className='mb-4'>
                  <div className='text-sm font-semibold text-neutral-700 mb-2'>
                    {t('pages.exploreLists.recommendedHeading')}
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {suggestedAffiliations.map((a) => (
                      a.id && a.name && (
                        <SuggestionChip
                          key={`suggested-${a.id}`}
                          label={a.name}
                          onAdd={(): Promise<void> => onSelectAffiliation({ id: a.id, name: a.name })}
                        />
                      )
                    ))}
                  </div>
                </div>
              )}

              <Typeahead
                placeholder={t('pages.exploreLists.searchGroupsPlaceholder')}
                search={searchAffiliations}
                onSelect={onSelectAffiliation}
                noMatchesHint={t('pages.exploreLists.noGroupMatch')}
              />
            </section>
          </div>
        </div>
      )}
    </div>
  );
});
